/**
 * Shared tone vocabulary for the component library (not a component — no default export).
 *
 * tone ∈ neutral | primary | warm | success | warning | danger
 *      | feed | sleep | nappy | activity | pump | health        (event identities)
 * Card also accepts 'default' (= neutral surface) and 'info' (= primary).
 *
 * Each map pairs a light recipe with its dark counterpart: soft tints use the single
 * sanctioned dark recipe `dark:bg-<tone>-900/20 dark:text-<tone>-300`, event tones use
 * their `event-*-dark` ground.
 */

export const normaliseTone = (tone) => {
  if (!tone || tone === 'default') return 'neutral';
  if (tone === 'info') return 'primary';
  if (tone === 'pumping') return 'pump';
  if (tone === 'accent') return 'health';
  return tone;
};

// Soft tinted chip/pill/banner surfaces (bg + text).
export const SOFT = {
  neutral: 'bg-surface-100 text-ink-muted dark:bg-night-card dark:text-night-muted',
  primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300',
  warm: 'bg-warm-50 text-warm-800 dark:bg-warm-900/20 dark:text-warm-300',
  success: 'bg-success-50 text-success-800 dark:bg-success-900/20 dark:text-success-300',
  warning: 'bg-warning-50 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
  danger: 'bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-300',
  feed: 'bg-event-feed-soft text-event-feed-dark dark:bg-event-feed-dark dark:text-warm-300',
  sleep: 'bg-event-sleep-soft text-event-sleep-dark dark:bg-event-sleep-dark dark:text-primary-300',
  nappy: 'bg-event-nappy-soft text-event-nappy-dark dark:bg-event-nappy-dark dark:text-success-300',
  activity: 'bg-event-activity-soft text-event-activity-dark dark:bg-event-activity-dark dark:text-warning-300',
  pump: 'bg-event-pump-soft text-event-pump-dark dark:bg-event-pump-dark dark:text-event-pump',
  health: 'bg-event-health-soft text-event-health-dark dark:bg-event-health-dark dark:text-accent-300',
};

// Hover state for soft surfaces (interactive pills / icon buttons).
export const SOFT_HOVER = {
  neutral: 'hover:bg-surface-200 dark:hover:bg-night-border',
  primary: 'hover:bg-primary-100 dark:hover:bg-primary-900/30',
  warm: 'hover:bg-warm-100 dark:hover:bg-warm-900/30',
  success: 'hover:bg-success-100 dark:hover:bg-success-900/30',
  warning: 'hover:bg-warning-100 dark:hover:bg-warning-900/30',
  danger: 'hover:bg-danger-100 dark:hover:bg-danger-900/30',
  feed: 'hover:bg-warm-100 dark:hover:bg-warm-900/30',
  sleep: 'hover:bg-primary-100 dark:hover:bg-primary-900/30',
  nappy: 'hover:bg-success-100 dark:hover:bg-success-900/30',
  activity: 'hover:bg-warning-100 dark:hover:bg-warning-900/30',
  pump: 'hover:bg-event-pump/20 dark:hover:bg-event-pump/20',
  health: 'hover:bg-accent-100 dark:hover:bg-accent-900/30',
};

// Card borders that pair with SOFT.
export const SOFT_BORDER = {
  neutral: 'border-surface-200 dark:border-night-border',
  primary: 'border-primary-100 dark:border-primary-800',
  warm: 'border-warm-100 dark:border-warm-800',
  success: 'border-success-100 dark:border-success-800',
  warning: 'border-warning-100 dark:border-warning-800',
  danger: 'border-danger-100 dark:border-danger-800',
  feed: 'border-event-feed/30 dark:border-event-feed/30',
  sleep: 'border-event-sleep/30 dark:border-event-sleep/30',
  nappy: 'border-event-nappy/30 dark:border-event-nappy/30',
  activity: 'border-event-activity/40 dark:border-event-activity/30',
  pump: 'border-event-pump/30 dark:border-event-pump/30',
  health: 'border-event-health/30 dark:border-event-health/30',
};

// Solid fills (dots, avatars, progress bars). White text on all but neutral/activity.
export const SOLID = {
  neutral: 'bg-surface-400 text-white dark:bg-surface-500',
  primary: 'bg-primary-600 text-white',
  warm: 'bg-warm-500 text-white',
  success: 'bg-success-600 text-white',
  warning: 'bg-warning-500 text-warm-900',
  danger: 'bg-danger-600 text-white',
  feed: 'bg-event-feed text-white',
  sleep: 'bg-event-sleep text-white',
  nappy: 'bg-event-nappy text-white',
  activity: 'bg-event-activity text-event-activity-dark',
  pump: 'bg-event-pump text-white',
  health: 'bg-event-health text-white',
};

// Foreground-only (icon / text colour) on a neutral surface.
export const TEXT = {
  neutral: 'text-ink-subtle dark:text-night-muted',
  primary: 'text-primary-600 dark:text-primary-400',
  warm: 'text-warm-600 dark:text-warm-400',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  danger: 'text-danger-600 dark:text-danger-400',
  feed: 'text-event-feed dark:text-event-feed',
  sleep: 'text-event-sleep dark:text-event-sleep',
  nappy: 'text-event-nappy dark:text-event-nappy',
  activity: 'text-event-activity dark:text-event-activity',
  pump: 'text-event-pump dark:text-event-pump',
  health: 'text-event-health dark:text-event-health',
};

// Focus ring shared by every interactive element.
export const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-night-surface';

export const PRESS = 'active:scale-[0.98] transition-[background-color,transform,box-shadow] duration-fast ease-out';

export const cx = (...parts) => parts.filter(Boolean).join(' ');
